import { useEffect, useState } from 'react';
import { api } from '../../api';

export default function HelpTickets({ opsRole }) {
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const canMutate = ['owner', 'admin', 'support'].includes(opsRole);

  const reload = () => {
    setTickets(null);
    api.get('/ops_admin/help_tickets', statusFilter ? { status: statusFilter } : null)
      .then((res) => setTickets(res.tickets))
      .catch((e) => setError(e.message));
  };

  useEffect(reload, [statusFilter]);

  const open = async (t) => {
    setSelected(t);
    setDetail(null);
    try {
      setDetail(await api.get(`/ops_admin/help_tickets/${t.id}`));
    } catch (e) {
      setError(e.message);
    }
  };

  const setStatus = async (newStatus) => {
    if (!selected) return;
    await api.patch(`/ops_admin/help_tickets/${selected.id}`, { status: newStatus });
    setSelected(null);
    setDetail(null);
    reload();
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 16 }}>
        <h1 style={{ fontSize: 24 }}>Help tickets</h1>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: 4 }}
        >
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="">All</option>
        </select>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!tickets && <p>Loading…</p>}

      {tickets && tickets.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>No tickets matching the filter.</p>
      )}

      {tickets && tickets.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>Created</th>
              <th style={{ padding: '8px 4px' }}>User</th>
              <th style={{ padding: '8px 4px' }}>Summary</th>
              <th style={{ padding: '8px 4px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr
                key={t.id}
                onClick={() => open(t)}
                style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
              >
                <td style={{ padding: '8px 4px' }}>{new Date(t.created_at).toLocaleString()}</td>
                <td style={{ padding: '8px 4px' }}>{t.user_id}</td>
                <td style={{ padding: '8px 4px' }}>{t.summary || <em>(no summary)</em>}</td>
                <td style={{ padding: '8px 4px' }}>{t.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
          }}
          onClick={() => { setSelected(null); setDetail(null); }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 8, padding: 20,
              width: 'min(800px, 90vw)', maxHeight: '85vh', overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 8 }}>Ticket {selected.id.slice(0, 8)}…</h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginBottom: 12 }}>
              {selected.user_id} · {new Date(selected.created_at).toLocaleString()} · status: {selected.status}
            </p>

            {selected.summary && (
              <p style={{ fontSize: 14, marginBottom: 12 }}>
                <strong>User summary:</strong> {selected.summary}
              </p>
            )}

            {!detail && <p>Loading detail…</p>}
            {detail && (
              <>
                <h4 style={{ marginTop: 12 }}>Context</h4>
                <pre style={{
                  background: 'var(--muted)', padding: 12, borderRadius: 4,
                  fontSize: 12, overflow: 'auto', maxHeight: 300,
                }}>
                  {JSON.stringify(
                    Object.fromEntries(Object.entries(detail.context).filter(([k]) => k !== 'transcript')),
                    null,
                    2,
                  )}
                </pre>

                <h4 style={{ marginTop: 12 }}>Transcript</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(detail.context?.transcript || []).map((m) => (
                    <div
                      key={m.id}
                      style={{
                        padding: 8, borderRadius: 4, fontSize: 13,
                        background: m.role === 'user' ? 'var(--color-primary)' : 'var(--muted)',
                        color: m.role === 'user' ? '#fff' : 'var(--color-text)',
                        whiteSpace: 'pre-wrap',
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                      }}
                    >
                      <strong>{m.role}:</strong> {m.content}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {canMutate && selected.status === 'open' && (
                <button onClick={() => setStatus('closed')}>Mark closed</button>
              )}
              {canMutate && selected.status === 'closed' && (
                <button onClick={() => setStatus('open')}>Reopen</button>
              )}
              <button onClick={() => { setSelected(null); setDetail(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
